import { useState } from "react";
import { useStore } from "../state/store";
import { ensembleSets } from "../engine/liveGuidance";
import { utcMs } from "../engine/intensity";
import type { StormBundle } from "../data/liveApi";
import { AdvisoryLog, LatestAdvisoryLine, TrendMetrics, ssCategory } from "./IntensityTrend";
import ReconPanel from "./ReconPanel";
import EnsembleBreakdown from "./EnsembleBreakdown";
import ActiveSystems from "./ActiveSystems";
import { stormIsPotential, systemName } from "../engine/liveSystems";

// Spread is judged RELATIVE to the typical track error at that lead time
// (ratio from the backend) — 50 nm of disagreement at 24h is alarming,
// 100 nm at 120h is routine.
function spreadColor(ratio: number): string {
  return ratio < 0.75 ? "#22C55E" : ratio < 1.4 ? "#FBBF24" : "#EF4444";
}
function spreadLabel(ratio: number): string {
  if (ratio < 0.75) return "Tighter than typical";
  if (ratio < 1.4) return "Typical spread";
  if (ratio < 2.0) return "Elevated";
  return "Large divergence";
}

// ── intensity guidance plume ──
// Anchored to the aids that RESOLVE or STATISTICALLY MODEL the core — NHC's
// intensity suite: regional core-resolving models (HAFS-A/B, HWRF, HMON) and
// the statistical-dynamical pair (SHIPS/LGEM) with HCCA as the highlighted
// consensus, and DeepMind's cyclone-trained ensemble mean (GDMN), which NHC
// folds into its IVCN/HCCA intensity consensus. Global models and global
// ensembles run ~25 km grids that cannot
// resolve a compact major hurricane's core and are systematically low-biased
// (Lowell, Cat 4 at 120 kt: GFS initialised at 84, GEFS/AIFS members 65-95),
// so the globals are drawn muted and never drive the band, and the ensemble
// fan is an opt-in scenario layer: read its SHAPE, never its level.
function IntensityPlume({ bundle }: { bundle: StormBundle }) {
  const [showEnsembles, setShowEnsembles] = useState(false);
  const TAUS = [0, 12, 24, 36, 48, 60, 72, 84, 96, 108, 120];

  type Trace = {
    tech: string; name: string; color: string; width: number; dash?: string; core: boolean;
    pts: { tau: number; vmax: number }[];
  };
  const traces: Trace[] = [];
  const runPts = (tech: string) =>
    (bundle.model_runs[tech]?.points ?? []).filter((p) => p.vmax > 0).map((p) => ({ tau: p.tau, vmax: p.vmax }));

  for (const t of ["HFSA", "HFSB", "HWRF", "HMON", "GDMN"]) {
    const r = bundle.model_runs[t];
    if (r) traces.push({ tech: t, name: r.tech_name.replace(/ \(retired.*\)/, ""), color: r.color, width: 1.4, core: true, pts: runPts(t) });
  }
  const STAT_COLOR: Record<string, string> = { DSHP: "#2DD4BF", LGEM: "#A3E635", HCCA: "#FDE68A" };
  for (const [t, r] of Object.entries(bundle.intensity_guidance ?? {})) {
    traces.push({
      tech: t, name: r.tech_name, color: STAT_COLOR[t] ?? "#9FB3C8",
      width: t === "HCCA" ? 2.6 : 1.4, core: true, pts: r.points,
    });
  }
  for (const t of ["AVNO", "EMXI"]) {
    const r = bundle.model_runs[t];
    if (r) traces.push({ tech: t, name: `${r.tech_name} (global)`, color: "#94A3B8", width: 1, dash: "2 3", core: false, pts: runPts(t) });
  }
  const plotted = traces.filter((t) => t.pts.length >= 2);
  const core = plotted.filter((t) => t.core);
  if (plotted.length === 0) return null;

  // band = range across the core aids at each lead time
  const band: { tau: number; lo: number; hi: number }[] = [];
  for (const tau of TAUS) {
    const vs = core.map((t) => t.pts.find((p) => p.tau === tau)?.vmax).filter((v): v is number => v != null && v > 0);
    if (vs.length >= 2) band.push({ tau, lo: Math.min(...vs), hi: Math.max(...vs) });
  }

  // opt-in global-ensemble fan (10-90% of all GLOBAL members). DeepMind's
  // cyclone-trained FNV3 members are deliberately left out: they are not
  // resolution-limited globals, so folding 1000 of them in would both
  // mislabel the fan and drown the GEFS/ENS/AIFS shape it exists to show.
  const globalSets = ensembleSets(bundle).filter((s) => s.source !== "FNV3");
  const members = showEnsembles ? globalSets.flatMap((s) => s.members) : [];
  const pct = (arr: number[], p: number) => {
    const a = [...arr].sort((x, y) => x - y);
    return a[Math.min(a.length - 1, Math.floor((p / 100) * a.length))];
  };
  const fanRows: { tau: number; p10: number; p50: number; p90: number }[] = [];
  for (const tau of TAUS) {
    const vs: number[] = [];
    for (const m of members) {
      const pt = m.points.find((p) => p.tau === tau);
      if (pt && pt.vmax > 0) vs.push(pt.vmax);
    }
    if (vs.length >= 5) fanRows.push({ tau, p10: pct(vs, 10), p50: pct(vs, 50), p90: pct(vs, 90) });
  }

  const W = 330, H = 130, PAD_L = 26, PAD_B = 14;
  const maxTau = 120;
  const allV = [
    ...plotted.flatMap((t) => t.pts.filter((p) => p.tau <= maxTau).map((p) => p.vmax)),
    ...bundle.official_forecast.map((p) => p.vmax),
    ...fanRows.map((r) => r.p90),
  ];
  const maxV = Math.max(140, ...allV) + 10;
  const x = (tau: number) => PAD_L + (tau / maxTau) * (W - PAD_L - 4);
  const y = (v: number) => (H - PAD_B) - (v / maxV) * (H - PAD_B - 6);
  const line = (pts: [number, number][]) => pts.map(([a, b], i) => `${i ? "L" : "M"}${a.toFixed(1)},${b.toFixed(1)}`).join(" ");
  const ribbon = (rows: { tau: number; lo: number; hi: number }[]) =>
    line(rows.map((r) => [x(r.tau), y(r.hi)] as [number, number])) + " " +
    line([...rows].reverse().map((r) => [x(r.tau), y(r.lo)] as [number, number])).replace(/^M/, "L") + " Z";
  const tracePath = (t: Trace) => line(t.pts.filter((p) => p.tau <= maxTau).map((p) => [x(p.tau), y(p.vmax)] as [number, number]));
  const ofclPts = bundle.official_forecast
    .filter((p) => p.tau <= maxTau && p.vmax > 0)
    .map((p) => [x(p.tau), y(p.vmax)] as [number, number]);

  // Saffir-Simpson thresholds (kt)
  const SS = [
    { v: 34, label: "TS" }, { v: 64, label: "1" }, { v: 83, label: "2" },
    { v: 96, label: "3" }, { v: 113, label: "4" }, { v: 137, label: "5" },
  ];
  const ensembleN = globalSets.reduce((n, s) => n + s.members.length, 0);

  return (
    <>
      <div className="muted xs" style={{ marginTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span>📈 INTENSITY GUIDANCE ({core.length} core aids)</span>
        {ensembleN >= 5 && (
          <button
            onClick={() => setShowEnsembles((v) => !v)}
            title="Global ensembles are resolution-limited and low-biased on strong storms — read the fan's shape, not its level"
            style={{
              padding: "1px 7px", borderRadius: 8, cursor: "pointer", textTransform: "none",
              border: `1px solid ${showEnsembles ? "#60A5FA" : "rgba(255,255,255,0.2)"}`,
              background: showEnsembles ? "rgba(96,165,250,0.18)" : "transparent",
              color: showEnsembles ? "white" : "rgba(255,255,255,0.55)", font: "600 10px Inter, sans-serif",
            }}
          >
            global ensembles ({ensembleN}): {showEnsembles ? "on" : "off"}
          </button>
        )}
      </div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ marginTop: 4 }}>
        {SS.map((s) => (
          <g key={s.v}>
            <line x1={PAD_L} x2={W - 4} y1={y(s.v)} y2={y(s.v)} stroke="rgba(255,255,255,0.10)" strokeDasharray="3 4" />
            <text x={2} y={y(s.v) + 3} fill="rgba(255,255,255,0.4)" fontSize="8">{s.label}</text>
          </g>
        ))}
        {fanRows.length >= 3 && (
          <>
            <path d={ribbon(fanRows.map((r) => ({ tau: r.tau, lo: r.p10, hi: r.p90 })))} fill="rgba(148,163,184,0.12)" stroke="rgba(148,163,184,0.3)" strokeWidth="0.8" />
            <path d={line(fanRows.map((r) => [x(r.tau), y(r.p50)] as [number, number]))} fill="none" stroke="rgba(148,163,184,0.7)" strokeWidth="1" strokeDasharray="3 2" />
          </>
        )}
        {band.length >= 2 && (
          <path d={ribbon(band)} fill="rgba(253,230,138,0.13)" stroke="rgba(253,230,138,0.35)" strokeWidth="0.8" />
        )}
        {plotted.filter((t) => !t.core).map((t) => (
          <path key={t.tech} d={tracePath(t)} fill="none" stroke={t.color} strokeWidth={t.width} strokeDasharray={t.dash} opacity={0.7} />
        ))}
        {plotted.filter((t) => t.core).map((t) => (
          <path key={t.tech} d={tracePath(t)} fill="none" stroke={t.color} strokeWidth={t.width} opacity={0.95} />
        ))}
        {ofclPts.length >= 2 && (
          <path d={line(ofclPts)} fill="none" stroke="#FFFFFF" strokeWidth="1.8" strokeDasharray="5 3" />
        )}
        {TAUS.filter((t) => t % 24 === 0).map((t) => (
          <text key={t} x={x(t)} y={H - 2} fill="rgba(255,255,255,0.45)" fontSize="8" textAnchor="middle">{t}h</text>
        ))}
      </svg>
      <div className="muted xs" style={{ lineHeight: 1.6 }}>
        {plotted.map((t) => (
          <span key={t.tech} style={{ color: t.core ? t.color : "#94A3B8", marginRight: 8, whiteSpace: "nowrap" }}>
            {t.core ? "▬" : "┄"} {t.name}
          </span>
        ))}
        <span style={{ color: "#fff", whiteSpace: "nowrap" }}>--- NHC official</span>
        {showEnsembles && <span style={{ color: "#94A3B8", marginLeft: 8 }}>▒ global-ensemble 10–90%</span>}
      </div>
      <div className="muted xs" style={{ marginTop: 2 }}>
        Shaded = range of the core aids (NHC's intensity suite). Global
        models/ensembles can't resolve a compact core and run low on strong
        storms — shown muted, never driving the band.
      </div>
    </>
  );
}

function parseUtc(iso: string): Date {
  // backend sends naive (UTC) timestamps — append Z so JS doesn't parse as local
  return new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : iso + "Z");
}

function formatAsOf(iso: string): string {
  const d = parseUtc(iso);
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "UTC",
  }) + "Z";
}

export default function LivePanel() {
  const bundle = useStore((s) => s.live.bundle);
  const loading = useStore((s) => s.live.loading);
  const advisories = useStore((s) => s.live.advisories);
  const recon = useStore((s) => s.live.recon);
  const showRecon = useStore((s) => s.live.showRecon);
  const toggleRecon = useStore((s) => s.toggleRecon);
  const selectedId = useStore((s) => s.live.selectedId);
  const error = useStore((s) => s.live.error);
  const selectedRef = useStore((s) => s.live.index.find((x) => x.storm_id === s.live.selectedId)
    ?? s.live.demoStorms.find((x) => x.storm_id === s.live.selectedId));
  const clearLiveStorm = useStore((s) => s.clearLiveStorm);

  // nothing open: the overview of every active system (the map's symbols)
  if (!selectedId) {
    return <div className="panel"><ActiveSystems /></div>;
  }

  const backLink = <button className="back-link" onClick={clearLiveStorm}>← All storms</button>;

  if (!bundle) {
    return (
      <div className="panel">
        {backLink}
        <div className="empty" style={{ textAlign: "center", paddingTop: 48 }}>
          <div style={{ fontSize: 40 }}>🛰️</div>
          <b style={{ display: "block", margin: "12px 0 6px" }}>
            {loading ? `Loading ${selectedRef ? systemName(selectedRef) : "storm"}…` : "Couldn't load this storm"}
          </b>
          <p className="muted">{loading ? "NHC forecast, model guidance and history." : error ?? "Try again in a moment."}</p>
        </div>
      </div>
    );
  }

  const last = bundle.best_track[bundle.best_track.length - 1];
  const isArchive = bundle.source.includes("archive");
  // a Potential Tropical Cyclone: advisories before it has formed
  const potential = stormIsPotential(bundle.storm_id, bundle.best_track.map((p) => p.storm_type), selectedRef);

  // Data-age honesty: when was this bundle fetched from NHC, and how old is
  // the newest best-track fix? A trader acting on a 6h-old fix during rapid
  // intensification needs to know it's 6h old.
  const fetchedMin = Math.max(0, Math.round((Date.now() - parseUtc(bundle.updated).getTime()) / 60000));
  const lastFixHours = last?.dt != null
    ? (Date.now() - parseUtc(last.dt).getTime()) / 3600000
    : null;
  const fixIsStale = !isArchive && lastFixHours != null && lastFixHours > 9;

  // newest official advisory in view (demo snapshots stop ~3 h after as_of,
  // when that init's advisory went out)
  const advCutoff = bundle.as_of ? (utcMs(bundle.as_of) ?? Infinity) + 3 * 3_600_000 : Infinity;
  const advInView = advisories.filter((a) => (utcMs(a.issued) ?? Infinity) <= advCutoff);
  const latestAdv = advInView[advInView.length - 1];
  // official motion beats the fix-derived speed when the advisory is current
  const advMotion = latestAdv?.move_dir && (isArchive || Date.now() - (utcMs(latestAdv.issued) ?? 0) < 7 * 3_600_000)
    ? latestAdv
    : null;

  return (
    <div className="panel">
      {backLink}
      <div className="live-header">
        <h3 style={{ margin: 0 }}>🌀 {potential ? `PTC ${bundle.name}` : bundle.name}</h3>
        <span className={isArchive ? "badge archive" : "badge live"}>
          {isArchive ? "ARCHIVE" : "● LIVE"}
        </span>
      </div>
      {bundle.stale && (
        <div className="error" style={{ marginTop: 6 }}>
          ⚠ NHC feed unreachable — showing last cached data.
        </div>
      )}
      {bundle.guidance_unavailable && (
        <div className="muted sm" style={{ marginTop: 6 }}>
          ℹ No model guidance published yet for this system — showing observed
          positions only. Invests usually pick up model runs once genesis
          chances rise; auto-refresh will catch them.
        </div>
      )}
      {bundle.as_of && (
        <div className="muted xs" style={{ marginTop: 4 }}>
          📍 Forecast snapshot · as of {formatAsOf(bundle.as_of)}
        </div>
      )}
      {!isArchive && (
        <div className="muted xs" style={{ marginTop: 4 }}>
          ⏱ Fetched {fetchedMin < 1 ? "just now" : `${fetchedMin} min ago`}
          {last?.dt && <> · last fix {formatAsOf(last.dt)}</>}
          {fixIsStale && (
            <span style={{ color: "#EF4444", fontWeight: 700 }}> · STALE ({Math.round(lastFixHours!)}h old)</span>
          )}
        </div>
      )}

      {/* wind AND pressure: change since the previous NHC update + 24 h,
          with the RI / rapid-deepening / rapid-weakening flags */}
      {potential && (
        <div className="muted sm" style={{ marginTop: 6, color: "#FBBF24" }}>
          ⚠ Potential Tropical Cyclone: not a tropical cyclone yet, but NHC expects one and is issuing
          advisories, with watches or warnings where it threatens land.
        </div>
      )}
      <TrendMetrics track={bundle.best_track} potential={potential} />
      <LatestAdvisoryLine adv={latestAdv} />
      {advMotion ? (
        <div className="muted xs" style={{ marginTop: 4 }}>
          → {advMotion.move_dir === "STNRY"
            ? "stationary (flood-risk multiplier)"
            : `moving ${advMotion.move_dir}${advMotion.move_deg != null ? ` (${advMotion.move_deg}°)` : ""} at ${advMotion.move_kt} kt`}
          {advMotion.move_kt != null && advMotion.move_kt > 0 && advMotion.move_kt < 4 ? " · nearly stationary (flood-risk multiplier)" : ""}
        </div>
      ) : last?.vf != null && last.vf > 0 && (
        <div className="muted xs" style={{ marginTop: 4 }}>
          → moving at {Math.round(last.vf)} kt
          {last.vf < 4 ? " · nearly stationary (flood-risk multiplier)" : ""}
        </div>
      )}

      <AdvisoryLog advisories={advisories} track={bundle.best_track} asOf={bundle.as_of} reconFixes={recon?.fixes} />

      {/* where the intensity is heading, right under where it has been */}
      <IntensityPlume bundle={bundle} />

      {bundle.official_forecast.length > 0 && (() => {
        const init = utcMs(bundle.official_init ?? bundle.latest_init);
        return (
          <>
            <div className="muted xs" style={{ marginTop: 14 }}>
              📋 NHC OFFICIAL FORECAST{init != null && <span style={{ textTransform: "none" }}> · from {formatAsOf(new Date(init).toISOString())}</span>}
            </div>
            {bundle.official_forecast.filter((p) => p.tau > 0 && p.tau % 12 === 0).map((p) => (
              <div className="fcst-row" key={p.tau}>
                <span className="muted" style={{ minWidth: 92 }}>
                  +{p.tau}h{init != null && <> · {new Date(init + p.tau * 3_600_000).toLocaleString("en-US", { weekday: "short", hour: "numeric" })}</>}
                </span>
                <span>{Math.abs(p.lat).toFixed(1)}°{p.lat < 0 ? "S" : "N"} {Math.abs(p.lon).toFixed(1)}°{p.lon < 0 ? "W" : "E"}</span>
                <span style={{ fontWeight: 600 }}>{p.vmax} kt · {ssCategory(p.vmax)}</span>
              </div>
            ))}
          </>
        );
      })()}

      {/* what each ensemble's members do with it: strength, and where they come ashore */}
      <EnsembleBreakdown bundle={bundle} />

      <ReconPanel recon={recon} show={showRecon} onToggle={toggleRecon} />

      {/* live Threat Fingerprint — the same five indices replays get */}
      {bundle.threat && (
        <>
          <div className="muted xs" style={{ marginTop: 14 }}>
            🧬 THREAT FINGERPRINT{" "}
            <b style={{ color: bundle.threat.overall[2], textTransform: "none" }}>
              {bundle.threat.overall[0]}/10 · {bundle.threat.overall[1]}
            </b>
          </div>
          {(["CDI", "IKE", "FSTI", "IFI", "SGM"] as const).map((k) => {
            const v = bundle.threat!.idx[k] ?? 0;
            const NAME: Record<string, string> = {
              CDI: "Core Destructiveness", IKE: "Integrated Kinetic Energy",
              FSTI: "Far-Field Tornado", IFI: "Inland Flooding", SGM: "Synoptic Gradient",
            };
            const col = v >= 8 ? "#E74C3C" : v >= 6 ? "#E67E22" : v >= 4 ? "#F1C40F" : "#2ECC71";
            return (
              <div key={k} style={{ marginTop: 5 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                  <span style={{ color: "rgba(255,255,255,0.7)" }}>{NAME[k]}</span>
                  <span style={{ color: col, fontWeight: 700 }}>
                    {v.toFixed(1)}/10 <span style={{ fontWeight: 400, color: "rgba(255,255,255,0.45)" }}>{bundle.threat!.labels[k]}</span>
                  </span>
                </div>
                <div style={{ height: 4, borderRadius: 2, background: "rgba(255,255,255,0.08)", marginTop: 2 }}>
                  <div style={{ height: 4, borderRadius: 2, width: `${(v / 10) * 100}%`, background: col }} />
                </div>
              </div>
            );
          })}
          <div className="muted xs" style={{ marginTop: 4 }}>
            Same engine + calibration as the 388 replay storms · the wind field
            on the map via the Wind toggle.
          </div>
        </>
      )}

      {Object.keys(bundle.spread).length > 0 && (
        <>
          <div className="muted xs" style={{ marginTop: 14 }}>📡 MODEL SPREAD</div>
          {Object.entries(bundle.spread).map(([tau, s]) => (
            <div className="spread-row" key={tau}>
              <span className="muted">τ+{tau}h</span>
              <span style={{ color: spreadColor(s.ratio), fontWeight: 600 }}>
                {spreadLabel(s.ratio)}{" "}
                <span className="muted" style={{ fontWeight: 400 }}>
                  ({s.spread_nm} nm vs ~{s.typical_nm} typical · n={s.n_models})
                </span>
              </span>
            </div>
          ))}
          <div className="muted xs" style={{ marginTop: 2 }}>
            Judged vs typical forecast error at each lead time. Low n = few
            aids still tracking the system — read "agreement" cautiously.
          </div>
          {(bundle.stale_aids?.length ?? 0) > 0 && (
            <div className="muted xs" style={{ marginTop: 4, color: "#FBBF24" }}>
              ⏳ Excluded stale runs:{" "}
              {bundle.stale_aids!.map((a) => `${a.tech_name} (−${Math.round(a.age_h)}h)`).join(", ")}
              {" "}— old inits don't mix with fresh guidance.
            </div>
          )}
          {(bundle.ensembles ?? []).some((e) => e.match_nm != null && e.match_nm > 120) && (
            <div className="muted xs" style={{ marginTop: 4 }}>
              🎯 Ensemble matched by position:{" "}
              {bundle.ensembles!.filter((e) => e.match_nm != null && e.match_nm > 120)
                .map((e) => `${e.source} ~${e.match_nm} nm`).join(", ")}
            </div>
          )}
          {Object.entries(bundle.ensemble_feeds ?? {}).some(([, f]) => f.status !== "ok") && (
            <div className="muted xs" style={{ marginTop: 4, color: "#FBBF24" }}>
              📡 Ensemble feeds:{" "}
              {Object.entries(bundle.ensemble_feeds!)
                .map(([src, f]) =>
                  f.status === "ok"
                    ? `${src} ok`
                    : f.status === "unavailable"
                      ? `${src} unavailable (upstream)`
                      : f.status === "no_match"
                        ? `${src} tracks this cycle, no cluster for this system`
                        : `${src} ${f.status}`)
                .join(" · ")}
            </div>
          )}
        </>
      )}

      <div className="muted xs center" style={{ marginTop: 14 }}>
        {Object.keys(bundle.model_runs).length} models · source: {bundle.source}
      </div>

      {/* life-safety deferral: this tool is context, NHC is the authority */}
      <div className="muted xs center" style={{ marginTop: 8 }}>
        For official watches, warnings & advisories:{" "}
        <a href="https://www.nhc.noaa.gov" target="_blank" rel="noreferrer" style={{ color: "#60A5FA" }}>
          nhc.noaa.gov ↗
        </a>
      </div>
    </div>
  );
}
